import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Magnet, 
  ArrowLeft, 
  Crown, 
  ShoppingCart, 
  CreditCard, 
  XCircle, 
  Loader2,
  CheckCircle,
  Calendar,
  Zap,
  AlertTriangle,
  Gift
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useUserPlan } from "@/hooks/useUserPlan";

export default function ManageSubscription() {
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [isLoadingCancel, setIsLoadingCancel] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [looseLeadsPrice, setLooseLeadsPrice] = useState<number | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { session, loading: authLoading, isAuthenticated } = useAuth();
  const { role, isLoading: planLoading, subscriptionEnd, cancelAtPeriodEnd, isGift, giftEnd } = useUserPlan();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/auth");
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!authLoading && !planLoading && role === "free") {
      navigate("/upgrade");
    }
  }, [authLoading, planLoading, role, navigate]);

  useEffect(() => {
    const loadSettings = async () => {
      const { data } = await supabase
        .from("platform_settings")
        .select("key, value")
        .eq("key", "loose_leads_price_brl")
        .maybeSingle();
      
      if (data?.value) {
        setLooseLeadsPrice(Number(data.value));
      }
    };
    loadSettings();
  }, []);

  const handleBuyLeads = async () => {
    setIsLoadingLeads(true);
    try {
      if (!session) {
        toast({
          title: "Faça login primeiro",
          description: "Você precisa estar logado para comprar leads.",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }

      const { data, error } = await supabase.functions.invoke("buy-leads", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, "_blank");
      } else {
        throw new Error("Não foi possível criar a sessão de checkout");
      }
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast({
        title: "Erro ao processar",
        description: error.message || "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingLeads(false);
    }
  };

  const handleOpenPortal = async () => {
    setIsLoadingPortal(true);
    try {
      if (!session) {
        toast({
          title: "Erro",
          description: "Sessão não encontrada.",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke("customer-portal", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      console.error("Portal error:", error);
      toast({
        title: "Erro",
        description: "Não foi possível abrir o portal.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsLoadingCancel(true);
    try {
      if (!session) {
        toast({
          title: "Erro",
          description: "Sessão não encontrada.",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke("cancel-subscription", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      setShowCancelDialog(false);
      toast({
        title: "Assinatura cancelada",
        description: data?.message || "Sua assinatura será encerrada ao final do período atual.",
      });

      // Refresh page after a moment
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      console.error("Cancel error:", error);
      toast({
        title: "Erro ao cancelar",
        description: error.message || "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCancel(false);
    }
  };

  if (authLoading || planLoading) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const formattedEndDate = subscriptionEnd 
    ? new Date(subscriptionEnd).toLocaleDateString("pt-BR", { 
        day: "2-digit", 
        month: "long", 
        year: "numeric" 
      })
    : null;

  const formattedGiftEnd = giftEnd
    ? new Date(giftEnd).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/dashboard">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-primary rounded-xl flex items-center justify-center shadow-glow">
              <Magnet className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="text-xl font-display font-bold">Magnet Link</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            {isGift ? (
              <Badge className="bg-gradient-primary text-white text-sm px-3 py-1">
                <Gift className="mr-1 h-4 w-4" />
                Pro Presente Ativo
              </Badge>
            ) : cancelAtPeriodEnd ? (
              <Badge variant="outline" className="border-destructive text-destructive text-sm px-3 py-1">
                <AlertTriangle className="mr-1 h-4 w-4" />
                Cancelamento agendado
              </Badge>
            ) : (
              <Badge className="bg-gradient-primary text-white text-sm px-3 py-1">
                <Crown className="mr-1 h-4 w-4" />
                Plano Pro Ativo
              </Badge>
            )}
          </div>
          <h1 className="text-3xl font-display font-bold mb-2">
            Gerenciar Assinatura
          </h1>
          {isGift ? (
            formattedGiftEnd && (
              <p className="text-muted-foreground flex items-center justify-center gap-2">
                <Calendar className="h-4 w-4" />
                Seu presente de 1 mês Pro vence em {formattedGiftEnd}
              </p>
            )
          ) : (
            formattedEndDate && (
              <p className="text-muted-foreground flex items-center justify-center gap-2">
                <Calendar className="h-4 w-4" />
                {cancelAtPeriodEnd
                  ? `Seu plano Pro fica ativo até ${formattedEndDate}`
                  : `Próxima cobrança: ${formattedEndDate}`}
              </p>
            )
          )}
          {!isGift && cancelAtPeriodEnd && (
            <p className="text-sm text-destructive mt-2">
              Sua assinatura já foi cancelada e não haverá novas cobranças.
            </p>
          )}
        </div>

        {/* Gift Upgrade Invite */}
        {isGift && (
          <Card className="border-2 border-primary bg-gradient-to-br from-primary/5 to-primary/10 mb-6">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center mb-4">
                <Crown className="h-8 w-8 text-primary-foreground" />
              </div>
              <CardTitle className="text-2xl font-display">
                Continue Pro depois do presente
              </CardTitle>
              <CardDescription className="text-base">
                {formattedGiftEnd
                  ? `Seu acesso gratuito termina em ${formattedGiftEnd}. Assine para não perder seus formulários e leads.`
                  : "Assine o plano Pro para manter todos os recursos após o período de presente."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                asChild
                size="lg"
                className="w-full bg-gradient-primary hover:opacity-90 text-lg py-6"
              >
                <Link to="/upgrade">
                  <Crown className="mr-2 h-5 w-5" />
                  Assinar o Plano Pro
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}



        {/* Leads Avulsos Card - DESTAQUE */}
        <Card className="border-2 border-primary bg-gradient-to-br from-primary/5 to-primary/10 mb-6">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center mb-4">
              <Zap className="h-8 w-8 text-primary-foreground" />
            </div>
            <CardTitle className="text-2xl font-display">
              Precisa de Mais Leads?
            </CardTitle>
            <CardDescription className="text-base">
              Compre pacotes de leads extras e turbine suas campanhas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center py-4">
              <div className="inline-flex items-baseline gap-1">
                <span className="text-4xl font-bold text-primary">
                  R$ {looseLeadsPrice ? (looseLeadsPrice / 100).toFixed(2).replace(".", ",") : "10,00"}
                </span>
                <span className="text-muted-foreground text-lg">/ 100 leads</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Pagamento único • Créditos nunca expiram
              </p>
            </div>

            <div className="flex flex-col gap-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>100 leads extras para usar quando quiser</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Sem assinatura adicional</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Perguntas por IA incluídas</span>
              </div>
            </div>

            <Button 
              onClick={handleBuyLeads}
              disabled={isLoadingLeads}
              size="lg"
              className="w-full bg-gradient-primary hover:opacity-90 text-lg py-6"
            >
              {isLoadingLeads ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  Comprar 100 Leads Agora
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <Separator className="my-8" />

        {/* Gerenciamento Section */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Gerenciamento da Conta</h2>
          
          {/* Ver Pagamentos */}
          <Card>
            <CardContent className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium">Histórico de Pagamentos</p>
                  <p className="text-sm text-muted-foreground">
                    Veja suas faturas e métodos de pagamento
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={handleOpenPortal}
                disabled={isLoadingPortal}
              >
                {isLoadingPortal ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Ver Pagamentos"
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Cancelar Assinatura */}
          <Card className="border-destructive/20">
            <CardContent className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                  <XCircle className="h-5 w-5 text-destructive" />
                </div>
                <div>
                  <p className="font-medium">Cancelar Assinatura</p>
                  <p className="text-sm text-muted-foreground">
                    {isGift
                      ? "Não há assinatura paga para cancelar (acesso via presente)"
                      : cancelAtPeriodEnd
                      ? `Cancelamento já solicitado${formattedEndDate ? ` • acesso até ${formattedEndDate}` : ""}`
                      : "Você pode cancelar a qualquer momento"}
                  </p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setShowCancelDialog(true)}
                disabled={isGift || cancelAtPeriodEnd}
              >
                {isGift ? "Indisponível" : cancelAtPeriodEnd ? "Cancelada" : "Cancelar"}
              </Button>

            </CardContent>
          </Card>
        </div>

        {/* Cancel Confirmation Dialog */}
        <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-2">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <AlertDialogTitle className="text-center">
                Tem certeza que deseja cancelar?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-center space-y-3">
                <p>
                  Ao confirmar, sua assinatura será cancelada e <strong>não haverá cobrança na próxima fatura</strong>.
                </p>
                <p className="text-destructive font-medium">
                  ⚠️ A cobrança vigente deste mês já realizada não será estornada.
                </p>
                <p>
                  Você continuará tendo acesso ao plano Pro até o final do período já pago
                  {formattedEndDate ? ` (${formattedEndDate})` : ""}.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="sm:flex-row gap-2">
              <AlertDialogCancel disabled={isLoadingCancel}>
                Manter Assinatura
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleCancelSubscription();
                }}
                disabled={isLoadingCancel}
                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              >
                {isLoadingCancel ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cancelando...
                  </>
                ) : (
                  "Sim, Cancelar Assinatura"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Back to Dashboard */}
        <div className="mt-8 text-center">
          <Button variant="ghost" asChild>
            <Link to="/dashboard">Voltar para o Dashboard</Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
